import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   Inject,
   OnDestroy,
   OnInit,
   ViewEncapsulation,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CallState } from '@lcs/shared/api';
import { Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { ExtensionTypes, TimeZones } from 'generated/graphql-types';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { FormComponent } from 'src/app/common-components/forms-base/form-component.interface';
import { getExtensionDetailSaveEvent } from 'src/app/common-components/helpers/extension-form-helper';
import { getExtensionPrivileges } from 'src/app/privileges/privileges.selector';
import { getSelectedAccountId, getSelectedAccountTimeZone } from 'src/app/store/select-account/select-account.selector';
import { ExtensionDetailView } from '../../extension-details/models/extension-detail-view.model';
import { ExtensionPrivileges } from '../../extension-details/models/extension-privileges';
import { ExtensionDetailFormService } from '../../extension-details/services/extension-detail-form.service';
import { createExtension, createExtensionSuccess } from '../state/add-extension.actions';
import { getAddExtensionCallState } from '../state/add-extension.selectors';
@Component({
   changeDetection: ChangeDetectionStrategy.OnPush,
   encapsulation: ViewEncapsulation.None,
   providers: [ExtensionDetailFormService],
   selector: 'ndt-add-extension',
   styleUrls: ['./add-extension.component.scss'],
   templateUrl: './add-extension.component.html',
   standalone: false,
})
export class AddExtensionComponent implements OnInit, OnDestroy, FormComponent {
   callState: Observable<CallState>;

   extensionPrivileges$: Observable<ExtensionPrivileges>;

   form: UntypedFormGroup;

   showView = true;

   extensionsForAdd: any | null;

   extensionGroupsForAdd: any | null;

   externalNumbers: any | null;

   get extension(): ExtensionDetailView {
      return this._extension;
   }

   set extension(value: ExtensionDetailView) {
      this._extension = value;
      if (value) {
         this.formService.updateExtension(value);
      }
   }

   private _extension: ExtensionDetailView;

   extensionTypes: typeof ExtensionTypes = ExtensionTypes;

   accountId: Observable<number | null>;

   timeZoneId: Observable<TimeZones | null>;

   private destroyed$ = new Subject<void>();

   constructor(
      protected store: Store,
      protected formService: ExtensionDetailFormService,
      @Inject(MAT_DIALOG_DATA) public data,
      private dialogService: LcsDialogService,
      private actions$: Actions,
      private changeDetectorRef: ChangeDetectorRef
   ) {
      this.accountId = store.select(getSelectedAccountId);
      this.timeZoneId = store.select(getSelectedAccountTimeZone);
      this.extension = {
         extensionId: 0,
         type: data.createExtensionTypeOption.createExtensionTypeOption,
      };

      this.extensionsForAdd = this.data.loadSuccessAction.extensionsConnection.items;
      this.externalNumbers = this.data.externalNumbers.externalNumberHistoriesConnection.items;
      const defaultAreaCode = this.extensionsForAdd[0]?.account.defaultAreaCode;
      if (defaultAreaCode) {
         this.formService.form.get('extensionPhone.defaultAreaCode')?.setValue(defaultAreaCode);
         this.formService.form.markAsPristine();
         this.formService.form.updateValueAndValidity();
      }
   }

   ngOnInit(): void {
      this.form = this.formService.form;

      this.callState = this.store.pipe(select(getAddExtensionCallState));
      this.extensionPrivileges$ = this.store.pipe(select(getExtensionPrivileges));
      this.extensionGroupsForAdd = this.data.loadSuccessAction.extensionGroupsConnection.items;
      this.externalNumbers = this.data.externalNumbers.externalNumberHistoriesConnection.items;
      this.extensionsForAdd = this.data.loadSuccessAction.extensionsConnection.items;
      this.formService.addUniqueExtensionNumberValidator(this.data.extensionNumbers);

      this.actions$.pipe(ofType(createExtensionSuccess), takeUntil(this.destroyed$)).subscribe((result) => {
         if (result.continuous) {
            this.resetForNextExtension(result.extensionNumber);
         }
      });
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onCloseClick(): void {
      this.dialogService.close();
   }

   onCreate(continuous = false): void {
      if (this.form.valid) {
         this.formService.form.get('extensionPhone.defaultAreaCode')?.markAsDirty();
         const extension = this.formService.getValue(null);
         const extensionSaveEvent = getExtensionDetailSaveEvent(extension, this.formService);
         extensionSaveEvent.extensionFields = [...extensionSaveEvent.extensionFields, 'accountId', 'type'];
         this.store.dispatch(
            createExtension({
               extensionSave: extensionSaveEvent,
               continuous,
            })
         );
      }
   }

   private resetForNextExtension(createdExtensionNumber: number): void {
      this.data.extensionNumbers = [...this.data.extensionNumbers, createdExtensionNumber];
      const nextType = this.extension.type;

      this.showView = false;
      this.changeDetectorRef.detectChanges();

      this.formService.resetForm();
      this.form = this.formService.form;

      this.extension = {
         extensionId: 0,
         type: nextType,
      };
      const defaultAreaCode = this.extensionsForAdd[0]?.account.defaultAreaCode;
      if (defaultAreaCode) {
         this.formService.form.get('extensionPhone.defaultAreaCode')?.setValue(defaultAreaCode);
      }
      this.formService.form.markAsPristine();
      this.formService.form.updateValueAndValidity();
      this.formService.addUniqueExtensionNumberValidator(this.data.extensionNumbers);
      this.showView = true;
      this.changeDetectorRef.detectChanges();
   }
}
